import React from 'react';
import { useDrop } from 'react-dnd';
import { ItemTypes, PuzzlePiece } from './PuzzlePiece';
import { cn } from '../../lib/utils';
import { GRID_SIZE } from '../../lib/constants';

export function BoardCell({ 
  row, 
  col, 
  piece, 
  pieceSize, 
  onDrop, 
  onSwap,
  isIncorrect,
  hintImageUrl,
  hintOpacity,
  gridSize = GRID_SIZE,
  // Tap mode props
  useTapMode = false,
  hasSelection = false,
  isSelected = false,
  onPieceTap,
  onCellTap,
  // Hint feature
  isHintTarget = false
}) {
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.PUZZLE_PIECE,
    drop: (item) => {
      // If dropping on a cell that has a piece, swap them
      if (piece && !piece.isCorrect) {
        onSwap(item.id, item.sourceRow, item.sourceCol, row, col, item.fromTray);
      } else if (!piece) {
        onDrop(item.id, row, col, item.fromTray, item.sourceRow, item.sourceCol);
      }
    },
    canDrop: (item) => {
      // Can drop if cell is empty OR has an incorrect piece (for swapping)
      // But not if dragging to the same cell
      if (item.sourceRow === row && item.sourceCol === col) return false;
      return !piece || !piece.isCorrect;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  }), [row, col, piece, onDrop, onSwap]);
  
  const showHint = hintOpacity > 0 && !piece;
  
  // Handle cell click in tap mode
  const handleCellClick = (e) => {
    if (!useTapMode) return;
    
    // If clicking on a piece, handle piece tap
    if (piece && !piece.isCorrect) {
      e.stopPropagation();
      onPieceTap?.();
      return;
    }
    
    // If we have a selection and clicking empty cell or swappable piece, handle placement
    if (hasSelection) {
      e.stopPropagation();
      onCellTap?.();
    }
  };
  
  return (
    <div
      ref={drop}
      onClick={handleCellClick}
      className={cn(
        "board-cell relative border border-border/50 transition-all duration-150 bg-card/90",
        isOver && canDrop && "can-drop bg-accent/30",
        piece && "has-piece",
        isIncorrect && "animate-shake",
        useTapMode && hasSelection && !piece && "cursor-pointer hover:bg-primary/10",
        useTapMode && hasSelection && piece && !piece.isCorrect && "cursor-pointer hover:bg-primary/10",
        isHintTarget && !piece && "ring-4 ring-yellow-400 ring-inset animate-pulse bg-yellow-400/30 shadow-[inset_0_0_20px_rgba(250,204,21,0.5)]"
      )}
      style={{
        width: pieceSize,
        height: pieceSize,
      }}
      data-row={row}
      data-col={col}
      data-correct={piece?.isCorrect ? 'true' : 'false'}
    >
      {/* Hint image background */}
      {showHint && (
        <div
          className="absolute inset-0 bg-no-repeat pointer-events-none"
          style={{
            backgroundImage: `url(${hintImageUrl})`,
            backgroundPosition: `${-col * pieceSize}px ${-row * pieceSize}px`,
            backgroundSize: `${pieceSize * gridSize}px ${pieceSize * gridSize}px`,
            opacity: hintOpacity,
          }}
        />
      )}
      
      {/* Tap mode: valid drop zone indicator when piece is selected */}
      {useTapMode && hasSelection && !piece && (
        <div className="absolute inset-0 border-2 border-dashed border-primary/40 rounded-sm pointer-events-none" />
      )}
      
      {/* Placed piece */}
      {piece && (
        <div className="absolute inset-0">
          <PuzzlePiece 
            piece={piece} 
            pieceSize={pieceSize} 
            isInTray={false}
            sourcePosition={{ row, col }}
            gridSize={gridSize}
            useTapMode={useTapMode}
            isSelected={isSelected}
            onTap={onPieceTap}
          />
          {/* Correct indicator (green) - rendered first, below red */}
          {piece.isCorrect && (
            <div 
              className="absolute pointer-events-none rounded-sm"
              style={{
                top: -2,
                left: -2,
                right: -2,
                bottom: -2,
                border: '2px solid #22c55e',
                zIndex: 1,
              }}
            />
          )}
          {/* Incorrect indicator (red) - rendered second, above green */}
          {!piece.isCorrect && !isSelected && (
            <div 
              className="absolute pointer-events-none rounded-sm"
              style={{
                top: -2,
                left: -2,
                right: -2,
                bottom: -2,
                border: '2px solid #ef4444',
                zIndex: 2,
              }}
            />
          )}
        </div>
      )}
      
      {/* Drop zone indicator */}
      {!piece && isOver && canDrop && (
        <div className="absolute inset-0 bg-primary/20 rounded-sm" />
      )}
      
      {/* Swap indicator when hovering over existing piece */}
      {piece && !piece.isCorrect && isOver && canDrop && (
        <div className="absolute inset-0 bg-primary/30 rounded-sm ring-2 ring-primary ring-inset" />
      )}
    </div>
  );
}
